// src/components/LanguageBar.jsx
import React from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { SUPPORTED_LANGS, normalizeLang } from "../i18n";
import { getSystemStrings } from "../strings/systemStrings";
import JourneyService from "../services/JourneyService";
import LegalFooterLinks from "./LegalFooterLinks";

const LANG_STORAGE_KEY = "innervoice_lang";

function useQueryLang() {
  const [searchParams, setSearchParams] = useSearchParams();

  const currentLang = normalizeLang(
    searchParams.get("lang") ||
      (typeof window !== "undefined" ? window.localStorage.getItem(LANG_STORAGE_KEY) : "he")
  );

  const setLang = (nextLang) => {
    const normalized = normalizeLang(nextLang);
    if (typeof window !== "undefined") {
      window.localStorage.setItem(LANG_STORAGE_KEY, normalized);
    }
    const next = new URLSearchParams(searchParams);
    next.set("lang", normalized);
    setSearchParams(next, { replace: true });
  };

  return [currentLang, setLang];
}

export default function LanguageBar() {
  const [lang, setLang] = useQueryLang();
  const location = useLocation();
  const navigate = useNavigate();
  const nav = getSystemStrings(lang).nav;
  const aboutLabel = nav.about;
  const chatLabel = nav.companion;
  const journeyLabel = nav.journey;
  const problemsLabel = nav.problems;
  const accountLabel = nav.account;

  const openJourneyHub = async () => {
    const currentParams = new URLSearchParams(location.search);
    const currentProblemKey = String(currentParams.get("problemKey") || "").trim();
    const currentRunId = String(currentParams.get("runId") || "").trim();
    if (currentProblemKey) {
      navigate(JourneyService.getOverviewTarget(lang, currentProblemKey, { runId: currentRunId }));
      return;
    }
    const mostRecentJourney = JourneyService.getMostRecentJourney(lang);
    if (mostRecentJourney?.problemKey) {
      navigate(JourneyService.getOverviewTarget(lang, mostRecentJourney.problemKey));
      return;
    }
    try {
      const res = await fetch("/api/billing/entitlements", { credentials: "include" });
      const data = await res.json().catch(() => null);
      const entitlements = Array.isArray(data?.entitlements) ? data.entitlements : [];
      if (entitlements.length > 0) {
        navigate(`/account?lang=${encodeURIComponent(lang)}`);
        return;
      }
    } catch {
      // fall through to problem selection
    }
    navigate(`/journey/select?lang=${encodeURIComponent(lang)}`);
  };

  return (
    <div className="lang-bar">
      <div className="lang-bar-main">
      <div className="lang-bar-group lang-group-left">
        {SUPPORTED_LANGS.map((code) => (
          <button
            key={code}
            type="button"
            className={`lang-pill ${lang === code ? "lang-pill-active" : ""}`}
            onClick={() => setLang(code)}
          >
            {code.toUpperCase()}
          </button>
        ))}
        <button
          type="button"
          className="lang-pill lang-pill-about"
          onClick={() => {
            const nextParams = new URLSearchParams(location.search);
            nextParams.set("lang", lang);
            navigate(`/about?${nextParams.toString()}`);
          }}
        >
          {aboutLabel}
        </button>
      </div>
      <div className="lang-bar-group lang-group-right">
        <button
          type="button"
          className="lang-pill lang-pill-about"
          onClick={openJourneyHub}
        >
          {journeyLabel}
        </button>
        <button
          type="button"
          className="lang-pill lang-pill-about"
          onClick={() => {
            const nextParams = new URLSearchParams();
            nextParams.set("lang", lang);
            try {
              if (typeof window !== "undefined" && window.localStorage?.getItem("innervoice_mobile") === "1") {
                nextParams.set("mobile", "1");
              }
            } catch {
              /* ignore */
            }
            navigate(`/journey/select?${nextParams.toString()}`);
          }}
        >
          {problemsLabel}
        </button>
        <button
          type="button"
          className="lang-pill lang-pill-about"
          onClick={() => {
            const nextParams = new URLSearchParams(location.search);
            nextParams.set("lang", lang);
            const currentProblemKey = String(nextParams.get("problemKey") || "").trim();
            const currentDay = String(nextParams.get("day") || "").trim();
            if (currentProblemKey) {
              navigate(
                JourneyService.getCompanionTarget(lang, currentProblemKey, {
                  day: currentDay,
                })
              );
              return;
            }
            const mostRecentJourney = JourneyService.getMostRecentJourney(lang);
            if (mostRecentJourney?.problemKey) {
              navigate(JourneyService.getCompanionTarget(lang, mostRecentJourney.problemKey));
              return;
            }
            navigate(`/journey/select?lang=${encodeURIComponent(lang)}`);
          }}
        >
          {chatLabel}
        </button>
        <button
          type="button"
          className="lang-pill lang-pill-about"
          onClick={() => {
            navigate(`/account?lang=${encodeURIComponent(lang)}`);
          }}
        >
          {accountLabel}
        </button>
      </div>
      </div>
      <LegalFooterLinks className="lang-bar-legal-row" />
    </div>
  );
}
